import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import PageLoader from './components/PageLoader';
import './App.css';

// Lazy load all pages for code splitting
// Core public pages (higher priority - prefetch hints)
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const StorePage = lazy(() => import('./pages/StorePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const BoiledPeanutsPage = lazy(() => import('./pages/BoiledPeanutsPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Delivery pages
const DeliveryPage = lazy(() => import('./pages/DeliveryPage'));
const DeliveryShopPage = lazy(() => import('./pages/DeliveryShopPage'));
const DeliveryCartPage = lazy(() => import('./pages/DeliveryCartPage'));
const DeliveryCheckoutPage = lazy(() => import('./pages/DeliveryCheckoutPage'));
const DeliveryConfirmationPage = lazy(() => import('./pages/DeliveryConfirmationPage'));
const DeliveryTrackPage = lazy(() => import('./pages/DeliveryTrackPage'));

// Customer pages
const CustomerAuthPage = lazy(() => import('./pages/CustomerAuthPage'));
const CustomerAccountPage = lazy(() => import('./pages/CustomerAccountPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));

// Loyalty pages
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const LoyaltyKioskPage = lazy(() => import('./pages/LoyaltyKioskPage'));
const LoyaltyLoginPage = lazy(() => import('./pages/LoyaltyLoginPage'));
const LoyaltyDashboardPage = lazy(() => import('./pages/LoyaltyDashboardPage'));
const LoyaltyResetPasswordPage = lazy(() => import('./pages/LoyaltyResetPasswordPage'));
const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage'));

// Membership pages
const MembershipPage = lazy(() => import('./pages/MembershipPage'));
const MembershipLoginPage = lazy(() => import('./pages/MembershipLoginPage'));
const MembershipDashboardPage = lazy(() => import('./pages/MembershipDashboardPage'));
const MembershipSuccessPage = lazy(() => import('./pages/MembershipSuccessPage'));
const MembershipResetPasswordPage = lazy(() => import('./pages/MembershipResetPasswordPage'));
const PickupTrackingPage = lazy(() => import('./pages/PickupTrackingPage'));

// Unified Auth pages
const UnifiedAuthPage = lazy(() => import('./pages/UnifiedAuthPage'));
const UnifiedForgotPasswordPage = lazy(() => import('./pages/UnifiedForgotPasswordPage'));
const UnifiedResetPasswordPage = lazy(() => import('./pages/UnifiedResetPasswordPage'));

// Admin pages (loaded only when needed)
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminDeliveryPage = lazy(() => import('./pages/AdminDeliveryPage'));const AdminProductsPage = lazy(() => import('./pages/AdminProductsPage'));
const AdminStoreOrdersPage = lazy(() => import('./pages/AdminStoreOrdersPage'));
const AdminCouponsPage = lazy(() => import('./pages/AdminCouponsPage'));
const AdminLoyaltyPage = lazy(() => import('./pages/AdminLoyaltyPage'));
const AdminSmsCampaignsPage = lazy(() => import('./pages/AdminSmsCampaignsPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));
const AdminMembershipPage = lazy(() => import('./pages/AdminMembershipPage'));
const AdminCampaignCenterPage = lazy(() => import('./pages/AdminCampaignCenterPage'));
const AdminCustomersPage = lazy(() => import('./pages/AdminCustomersPage'));
const AdminResetPasswordPage = lazy(() => import('./pages/AdminResetPasswordPage'));
const AdminPanelPage = lazy(() => import('./pages/AdminPanelPage'));

// Financial OS pages
const FinancialDashboard = lazy(() => import('./pages/FinancialDashboard'));
const FinancialTransactions = lazy(() => import('./pages/FinancialTransactions'));
const FinancialAIEntry = lazy(() => import('./pages/FinancialAIEntry'));
const FinancialReports = lazy(() => import('./pages/FinancialReports'));
const FinancialScenarios = lazy(() => import('./pages/FinancialScenarios'));
const FinancialSeasons = lazy(() => import('./pages/FinancialSeasons'));
const FinancialBusinessIntelligence = lazy(() => import('./pages/FinancialBusinessIntelligence'));
const FinancialPartnerPortal = lazy(() => import('./pages/FinancialPartnerPortal'));
const FinancialCalendar = lazy(() => import('./pages/FinancialCalendar'));
const FinancialEmailSettings = lazy(() => import('./pages/FinancialEmailSettings'));
const FinancialSeasonings = lazy(() => import('./pages/FinancialSeasonings'));

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Core public pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about-us" element={<AboutPage />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/product/:productSlug" element={<ProductDetailPage />} />
            <Route path="/boiled-peanuts" element={<BoiledPeanutsPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            
            {/* Delivery routes */}
            <Route path="/delivery" element={<DeliveryPage />} />
            <Route path="/delivery/shop" element={<DeliveryShopPage />} />
            <Route path="/delivery/cart" element={<DeliveryCartPage />} />
            <Route path="/delivery/checkout" element={<DeliveryCheckoutPage />} />
            <Route path="/delivery/confirmation/:orderNumber" element={<DeliveryConfirmationPage />} />
            <Route path="/delivery/track" element={<DeliveryTrackPage />} />
            <Route path="/delivery/login" element={<CustomerAuthPage />} />
            <Route path="/delivery/account" element={<CustomerAccountPage />} />
            <Route path="/delivery/password-reset" element={<PasswordResetPage />} />
            
            {/* Loyalty routes */}
            <Route path="/loyalty" element={<LoyaltyPage />} />
            <Route path="/loyalty/kiosk" element={<LoyaltyKioskPage />} />
            <Route path="/loyalty/login" element={<LoyaltyLoginPage />} />
            <Route path="/loyalty/dashboard" element={<LoyaltyDashboardPage />} />
            <Route path="/loyalty/reset-password" element={<LoyaltyResetPasswordPage />} />
            <Route path="/referral/:slug" element={<ReferralLandingPage />} />
            
            {/* Membership routes */}
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/membership/login" element={<MembershipLoginPage />} />
            <Route path="/membership/dashboard" element={<MembershipDashboardPage />} />
            <Route path="/membership/success" element={<MembershipSuccessPage />} />
            <Route path="/membership/reset-password" element={<MembershipResetPasswordPage />} />
            <Route path="/pickup-tracking" element={<PickupTrackingPage />} />
            
            {/* Unified Auth Routes */}
            <Route path="/account/login" element={<UnifiedAuthPage />} />
            <Route path="/account/signup" element={<UnifiedAuthPage />} />
            <Route path="/account/forgot-password" element={<UnifiedForgotPasswordPage />} />
            <Route path="/account/reset-password" element={<UnifiedResetPasswordPage />} />
            
            {/* Admin routes */}
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/delivery" element={<AdminDeliveryPage />} />
            <Route path="/admin/store-orders" element={<AdminStoreOrdersPage />} />
            <Route path="/admin/coupons" element={<AdminCouponsPage />} />
            <Route path="/admin/products" element={<AdminProductsPage />} />
            <Route path="/admin/loyalty" element={<AdminLoyaltyPage />} />
            <Route path="/admin/membership" element={<AdminMembershipPage />} />
            <Route path="/admin/customers" element={<AdminCustomersPage />} />
            <Route path="/admin/sms-campaigns" element={<AdminSmsCampaignsPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="/admin/campaigns" element={<AdminCampaignCenterPage />} />
            <Route path="/admin/panel" element={<AdminPanelPage />} />
            <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
            
            {/* Financial OS routes */}
            <Route path="/admin/financial-os" element={<FinancialDashboard />} />
            <Route path="/admin/financial-os/transactions" element={<FinancialTransactions />} />
            <Route path="/admin/financial-os/ai-entry" element={<FinancialAIEntry />} />
            <Route path="/admin/financial-os/reports" element={<FinancialReports />} />
            <Route path="/admin/financial-os/scenarios" element={<FinancialScenarios />} />
            <Route path="/admin/financial-os/seasons" element={<FinancialSeasons />} />
            <Route path="/admin/financial-os/business-intelligence" element={<FinancialBusinessIntelligence />} />
            <Route path="/admin/financial-os/partner-portal" element={<FinancialPartnerPortal />} />
            <Route path="/admin/financial-os/calendar" element={<FinancialCalendar />} />
            <Route path="/admin/financial-os/email-recap" element={<FinancialEmailSettings />} />
            <Route path="/admin/financial-os/seasonings" element={<FinancialSeasonings />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
